import { useState, ReactNode } from 'react';

import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Card as MUICard,
  CardContent,
  CardHeader,
  CardHeaderProps as MUICardHeaderProps,
  CardProps as MUICardProps,
  Box,
  Divider,
  CardActionArea,
  SxProps,
  Theme,
} from '@mui/material';

import { Error } from '..';
import { cardStyles } from './Card.styles';

export interface CardProps extends Omit<MUICardProps, 'title'> {
  title?: ReactNode;
  error?: string;
  expandable?: boolean;
  sx?: SxProps<Theme>;
  headerStyle?: SxProps<Theme>;
  gutterBottom?: boolean;
  contentClasses?: any;
  contentSX?: SxProps<Theme>;
  muiHeaderProps?: MUICardHeaderProps;
}

const Card = ({
  title,
  error,
  expandable,
  sx,
  gutterBottom,
  children,
  contentClasses,
  contentSX,
  muiHeaderProps,
  headerStyle,
  ...muiCardProps
}: CardProps) => {
  //if the card is expandable it should be closed by default
  const [expanded, setExpanded] = useState(expandable ? false : true);

  const Header = () => (
    <CardHeader
      {...muiHeaderProps}
      title={title}
      titleTypographyProps={{ variant: 'h5' }}
      sx={{ ...cardStyles.header, ...headerStyle }}
      role="heading"
      data-testid="card-heading"
      action={
        expandable ? (
          <Box display="flex" gap={1}>
            {muiHeaderProps?.action}
            {expanded ? <ArrowDropUp /> : <ArrowDropDown />}
          </Box>
        ) : (
          muiHeaderProps?.action
        )
      }
    />
  );
  return (
    <MUICard
      elevation={0}
      sx={{
        ...cardStyles.root,
        ...(gutterBottom ? cardStyles.gutterBottom : {}),
        ...(sx ? sx : {}),
      }}
      {...muiCardProps}
    >
      {(title || expandable || muiHeaderProps?.action) &&
        (expandable ? (
          <CardActionArea
            disableRipple
            sx={cardStyles.actionArea}
            onClick={() => setExpanded(!expanded)}
          >
            <Header />
          </CardActionArea>
        ) : (
          <Header />
        ))}
      {expanded && (error || children) && (
        <>
          {(title || expandable || muiHeaderProps?.action) && <Divider sx={cardStyles.divider} />}
          <CardContent
            className={contentClasses}
            sx={{ ...cardStyles.content, ...(contentSX ? contentSX : {}) }}
          >
            {error && <Error error={error} />}
            {children}
          </CardContent>
        </>
      )}
    </MUICard>
  );
};

export default Card;
